import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { DrawerBodyWrapper, DrawerFooterWrapper } from '@base'
import CreateItemForm from './CreateItemForm'
import { ActionButton } from '@pages/Admin/SalesPurchaseOrder2/style'
import { enquiryInputType, strings } from '@constants'
import { toggleDrawer } from '@data/state/action/root'
import { getProductByCategory } from '@data'
import { showLoader } from '@data/state/action'
import { generateItemId } from '@utils'

const deriveFormState = (data = null, categories = [], brands = []) => {
	const initialFormState = {
		category: {
			label: strings('category'),
			disabled: false,
			readOnly: false,
			displayKey: 'category_name',
			primaryKey: 'category_id',
			placeholder: strings('select_a_category'),
			data: [],
			value: '',
			dirty: false,
			shouldFetchProducts: { flag: true },
		},
		product: {
			label: strings('product'),
			disabled: false,
			readOnly: false,
			displayKey: 'product_name',
			primaryKey: 'product_id',
			placeholder: strings('search_product'),
			data: [],
			value: '',
			dirty: false,
		},
		grade: {
			label: strings('grade'),
			placeholder: strings('enter', 'grade'),
			readOnly: false,
			value: '',
			dirty: false,
		},
		brand: {
			label: strings('brand'),
			disabled: false,
			readOnly: false,
			displayKey: 'name',
			primaryKey: 'id',
			placeholder: strings('select_brand'),
			data: [],
			value: '',
			dirty: false,
		},
		tc_required: {
			label: strings('tc_reqd'),
			disabled: false,
			value: true,
			dirty: false,
		},
		inputType: {
			label: strings('type'),
			disabled: false,
			value: '',
			dirty: false,
		},
		hsn_code: {
			label: strings('hsn', 'code'),
			placeholder: strings('enter', 'hsn', 'code'),
			disabled: true,
			readOnly: false,
			value: '',
			dirty: false,
		},
		price_per_kg: {
			label: strings('price_per_kg'),
			disabled: false,
			value: '',
			dirty: false,
		},
		allotted_weight: {
			label: strings('weight'),
			disabled: false,
			value: '',
			dirty: false,
		},
	}
	const _state = initialFormState
	_state.category.data = categories
	_state.brand.data = brands
	if (!data) {
		return _state
	} else {
		_state.category.value = categories?.find((item) => item?.category_id === data?.category_id)
		_state.product.value = { product_id: data?.product_id, product_name: data?.product_name }
		_state.brand.value = brands?.find((item) => item?.name === data?.brand_ordered)
		_state.allotted_weight.value =
			data?.allotted_weight && data?.input_type === enquiryInputType?.WEIGHT ? data?.allotted_weight / 1000 : data?.allotted_weight
		_state.price_per_kg.value = data?.price_per_kg
		_state.hsn_code.value = data?.hsn_code
		_state.inputType.value = data?.input_type
		_state.tc_required.value = data?.tc_available
		_state.grade.value = data?.grade
	}
	return _state
}

const SPOItemForm = ({ edit = false, itemId, state, addNewItemCallback, deleteSpoItemCallback, updateSpoItemCallback, dispatch, getState }) => {
	const globalDispatch = useDispatch()
	const { categories, brands } = state
	const orderItems = state?.newSpo?.get('order_items')
	const itemData = edit ? orderItems?.find((_i) => _i.item_id === itemId) : null
	const [formState, setFormState] = useState(deriveFormState(itemData, categories?.data?.data, brands?.data))
	const lastCategoryProductsFetched = useRef()

	const isScrap = false //commodity check

	useEffect(() => {
		const categoryId = formState?.category.value?.category_id
		if (categoryId && categoryId !== lastCategoryProductsFetched?.current) {
			lastCategoryProductsFetched.current = categoryId
			globalDispatch(showLoader(true))
			getProductByCategory(categoryId)
				.then((data) => {
					let updates = { data }
					if (!edit) {
						updates = { ...updates, value: '' }
					}
					updateFormState('product', updates)
				})
				.finally((e) => {
					globalDispatch(showLoader(false))
				})
		}
	}, [formState?.category, itemId])

	useEffect(() => {
		if (!edit) {
			setFormState(deriveFormState(null, categories?.data?.data, brands?.data))
		}
	}, [itemId, edit])

	const updateFormState = (key, updates) => {
		setFormState((_state) => ({
			..._state,
			[key]: {
				..._state[key],
				...updates,
			},
		}))
	}

	const formStateToObject = () => {
		return {
			allotted_weight:
				formState?.inputType?.value !== enquiryInputType.WEIGHT
					? formState?.allotted_weight?.value
					: formState?.allotted_weight?.value * 1000,
			brand_ordered: formState?.brand?.value?.name,
			category_id: formState?.category?.value?.category_id,
			category_name: formState?.category?.value?.category_name,
			grade: formState?.grade?.value === '' ? null : formState?.grade?.value,
			hsn_code: formState?.hsn_code?.value,
			input_type: formState?.inputType?.value,
			price_per_kg: formState?.price_per_kg?.value,
			product_id: formState?.product?.value?.product_id,
			product_name: formState?.product?.value?.product_name,
			tc_available: formState?.tc_required?.value,
			item_id: itemId ?? generateItemId(formState?.product?.value?.product_id, formState?.grade?.value),
		}
	}
	const addItemHandler = () => {
		const _orderItem = formStateToObject()
		addNewItemCallback(_orderItem)
		globalDispatch(toggleDrawer(false))
	}

	const updateItemHandler = () => {
		const _orderItem = formStateToObject()
		updateSpoItemCallback(itemId, _orderItem)
		globalDispatch(toggleDrawer(false))
	}

	const deleteButtonHandler = () => {
		deleteSpoItemCallback(itemId)
		globalDispatch(toggleDrawer(false))
	}

	const cancelEditHandler = () => {
		globalDispatch(toggleDrawer(false))
	}

	const addDisableButton = () => {
		return (
			!formState?.allotted_weight?.value ||
			!formState?.brand?.value ||
			!formState?.category?.value ||
			!formState?.hsn_code?.value ||
			!formState?.product?.value ||
			!formState?.price_per_kg?.value
		)
	}

	const updatedisableButton = () => {
		return false
	}
	const renderFooter = () => {
		if (!itemId) {
			return (
				<ActionButton small type='primary' disabled={addDisableButton()} onClick={() => addItemHandler()}>
					{strings('add_item')}
				</ActionButton>
			)
		} else if (itemId && edit) {
			return (
				<>
					<ActionButton small disabled={updatedisableButton()} type='primary' onClick={() => updateItemHandler()}>
						{strings('update')}
					</ActionButton>
					<ActionButton small type='tertiary' onClick={cancelEditHandler}>
						{strings('cancel')}
					</ActionButton>
					{/* <ActionButton margin={`0 auto 0 0`} small type='primary' variant='destructive' onClick={deleteButtonHandler}>
						{strings('delete')}
					</ActionButton> */}
				</>
			)
		}
	}

	return (
		<>
			<DrawerBodyWrapper>
				<CreateItemForm edit={edit} formState={formState} updateFormState={updateFormState} isScrap={isScrap} />
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>{renderFooter()}</DrawerFooterWrapper>
		</>
	)
}

export default SPOItemForm
